import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { trackEvent, trackEventOnce } from "@/lib/analytics";

// Lazy-loaded singleton so the supabase client chunk doesn't ship in the
// initial bundle. Resolved once on the first auth-related effect.
let supabasePromise: Promise<typeof import("@/integrations/supabase/client")["supabase"]> | null = null;
const loadSupabase = () => {
  if (!supabasePromise) {
    supabasePromise = import("@/integrations/supabase/client").then((m) => m.supabase);
  }
  return supabasePromise;
};

interface AuthCtx {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>({} as AuthCtx);

const getAuthMethod = (user: User) => {
  const provider = user.app_metadata?.provider;
  if (typeof provider === "string" && provider.length > 0) return provider === "email" ? "password" : provider;
  const identityProvider = user.identities?.[0]?.provider;
  return identityProvider === "email" ? "password" : identityProvider || "unknown";
};

const clearAuthAnalyticsSession = () => {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith("gtm_login_") || key.startsWith("gtm_signup_"))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* ignore analytics storage errors */
  }
};

const trackSignedIn = (user: User) => {
  const method = getAuthMethod(user);
  try {
    trackEventOnce(`gtm_login_${user.id}`, "login", { method });

    const createdAt = Date.parse(user.created_at);
    const lastSignInAt = Date.parse(user.last_sign_in_at || "");
    const looksLikeFirstOAuthLogin = method !== "password" && Number.isFinite(createdAt) && Number.isFinite(lastSignInAt) && Math.abs(lastSignInAt - createdAt) < 60_000;
    if (looksLikeFirstOAuthLogin) trackEventOnce(`gtm_signup_${user.id}`, "sign_up", { method });
  } catch {
    trackEvent("login", { method });
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let lastUserId: string | null = null;
    let unsub: (() => void) | null = null;
    let cancelled = false;
    const w: any = window;
    let idleId: any;
    let timeoutId: any;

    const boot = async () => {
      const supabase = await loadSupabase();
      if (cancelled) return;
      const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
        setSession(s);
        const nextUser = s?.user ?? null;
        const nextId = nextUser?.id ?? null;
        setUser(nextUser);
        if (_e === "SIGNED_IN" && nextUser) trackSignedIn(nextUser);
        if (_e === "SIGNED_OUT") clearAuthAnalyticsSession();
        if (nextId === lastUserId) return;
        lastUserId = nextId;
        if (nextUser) {
          setTimeout(async () => {
            const { data } = await supabase.from("user_roles").select("role").eq("user_id", nextUser.id).eq("role", "admin").maybeSingle();
            setIsAdmin(!!data);
          }, 0);
        } else {
          setIsAdmin(false);
        }
      });
      unsub = () => subscription.unsubscribe();
      supabase.auth.getSession().then(({ data: { session: s } }) => {
        setSession(s);
        setUser(s?.user ?? null);
        if (s?.user) trackSignedIn(s.user);
        setLoading(false);
      });
    };

    // Defer Supabase auth bootstrap until the browser is idle OR the user
    // performs an action that may need auth. Keeps auth-js / realtime /
    // postgrest out of the LCP critical path for anonymous visitors.
    const trigger = () => {
      if (idleId && w.cancelIdleCallback) w.cancelIdleCallback(idleId);
      if (timeoutId) clearTimeout(timeoutId);
      idleId = null; timeoutId = null;
      boot();
    };
    if (typeof w.requestIdleCallback === "function") {
      idleId = w.requestIdleCallback(trigger, { timeout: 4000 });
    } else {
      timeoutId = setTimeout(trigger, 2500);
    }
    const events = ["pointerdown", "keydown", "touchstart"] as const;
    events.forEach((e) => window.addEventListener(e, trigger, { once: true, passive: true }));

    return () => {
      cancelled = true;
      unsub?.();
      if (idleId && w.cancelIdleCallback) w.cancelIdleCallback(idleId);
      if (timeoutId) clearTimeout(timeoutId);
      events.forEach((e) => window.removeEventListener(e, trigger));
    };
  }, []);

  return <Ctx.Provider value={{ user, session, isAdmin, loading, signOut: async () => { clearAuthAnalyticsSession(); const supabase = await loadSupabase(); await supabase.auth.signOut(); } }}>{children}</Ctx.Provider>;
};

export const useAuth = () => useContext(Ctx);
